import { useState } from 'react';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
import { ValidatePromotionalCodeQuery } from '@/features/subscription/graphql/ValidatePromotionalCodeQuery';
import type { ValidatePromotionalCodeQuery as ValidatePromotionalCodeQueryType } from '@/__generated__/ValidatePromotionalCodeQuery.graphql';

export interface DiscountInfo {
  effectiveValueCents: number | null;
  discountLabel: string | null;
  discountDuration: string | null;
  durationMonths: number | null;
}

interface PromoCodeInputProps {
  promotionalCode: string | null;
  onApplied: (args: { code: string; discount: DiscountInfo }) => void;
  onRemoved: () => void;
  planSlug: string;
  valueCents: number;
}

export function PromoCodeInput({ promotionalCode, onApplied, onRemoved, planSlug, valueCents }: PromoCodeInputProps) {
  const environment = useRelayEnvironment();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(false);

  if (promotionalCode && applied) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 flex items-center justify-between">
        <div className="text-xs text-green-700">
          <span className="font-semibold">{promotionalCode}</span> aplicado
        </div>
        <button
          type="button"
          onClick={() => { onRemoved(); setApplied(false); }}
          className="text-xs text-green-600 hover:text-green-800 font-semibold"
        >
          Remover
        </button>
      </div>
    );
  }

  const handleApply = async () => {
    if (!code.trim()) return;
    setError(null);
    setLoading(true);
    try {
      const result = await fetchQuery<ValidatePromotionalCodeQueryType>(
        environment,
        ValidatePromotionalCodeQuery,
        { code: code.trim() },
      ).toPromise();

      const plan = result?.validatePromotionalCode?.plans.find((p) => p.slug === planSlug);
      if (plan) {
        const discount: DiscountInfo = {
          effectiveValueCents: plan.effectiveValueCents ?? null,
          discountLabel: plan.discountLabel ?? null,
          discountDuration: plan.discountDuration ?? null,
          durationMonths: plan.durationMonths ?? null,
        };
        setApplied(true);
        onApplied({ code: code.trim(), discount });
        setCode('');
      } else if (result?.validatePromotionalCode?.valid) {
        setError('Código não se aplica a este plano');
      } else {
        setError('Código inválido ou expirado');
      }
    } catch {
      setError('Erro ao validar código');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Código cupom"
          className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs uppercase focus:outline-none focus:ring-2 focus:ring-primary/30"
          onKeyDown={(e) => e.key === 'Enter' && handleApply()}
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={loading || !code.trim()}
          className="rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[var(--color-primary-strong)] disabled:opacity-50 transition"
        >
          {loading ? '...' : 'Aplicar'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
